// ── Selected-map-object synchronisation ──
// Extracted verbatim from MapCanvas.tsx as TWO hooks, each called from its
// original position (an onboarding hook runs between them and that order is
// load-bearing).
//
// (1) `useSelectedMapObjectSync` keeps the selected object in step with the
//     latest mapObjects snapshot — updating it when it really changed, tearing
//     down the gold highlight on a type change, and clearing the selection when
//     the object is genuinely gone (never mid-fetch, never with a modal open).
// (2) `useReturnToMapObjectRestore` re-opens the house modal after coming back
//     from the interior.
//
// Bodies are byte-identical to the originals; every closure dependency is a
// parameter under its ORIGINAL name.
import { useEffect, type MutableRefObject } from 'react';
import type { MapObject } from '../../models/mapObject';
import { mapSceneSingleton } from './mapScene';
import { asTickerBound } from './render/mapPixiExtensions';

export interface UseSelectedMapObjectSyncParams {
  /** By value — effect deps. */
  selectedMapObject: MapObject | null;
  mapObjects: MapObject[];
  isPending: boolean;
  isFetching: boolean;
  isRealEstateModalOpen: boolean;
  isPlotFreeModalOpen: boolean;
  isRealEstateInfoModalOpen: boolean;
  isMapObjectModalOpen: boolean;
  sceneRef: MutableRefObject<import('./mapSceneState').MapSceneState>;
  setSelectedMapObject: (obj: MapObject | null) => void;
}

export function useSelectedMapObjectSync({
  selectedMapObject,
  mapObjects,
  isPending,
  isFetching,
  isRealEstateModalOpen,
  isPlotFreeModalOpen,
  isRealEstateInfoModalOpen,
  isMapObjectModalOpen,
  sceneRef,
  setSelectedMapObject,
}: UseSelectedMapObjectSyncParams): void {
  useEffect(() => {
    if (!selectedMapObject) return;

    const updatedMapObject = mapObjects.find(obj => obj.id === selectedMapObject.id);
    if (updatedMapObject) {
      // Only update if the object actually changed (avoid cascading re-renders from reference changes)
      const changed = updatedMapObject.type !== selectedMapObject.type
        || updatedMapObject.x !== selectedMapObject.x
        || updatedMapObject.y !== selectedMapObject.y
        || updatedMapObject.owner_id !== selectedMapObject.owner_id
        || updatedMapObject.mirror_direction !== selectedMapObject.mirror_direction;
      if (changed) {
        setSelectedMapObject(updatedMapObject);
        if (updatedMapObject.type !== selectedMapObject.type && sceneRef.current.sprites.mapObjectHighlight && sceneRef.current.layers.highlight) {
          const hl = asTickerBound(sceneRef.current.sprites.mapObjectHighlight);
          if (sceneRef.current.pixi.app && hl?.tickerFunction) {
            sceneRef.current.pixi.app.ticker.remove(hl.tickerFunction);
          }
          sceneRef.current.layers.highlight.removeChild(sceneRef.current.sprites.mapObjectHighlight);
          sceneRef.current.sprites.mapObjectHighlight.destroy();
          sceneRef.current.sprites.mapObjectHighlight = null;
          mapSceneSingleton.mapObjectHighlight = null;
        }
      }
      return;
    }

    // Об'єкта немає в поточному snapshot — не завжди означає "видалили з мапи":
    // - перший запит / рефетч: масив може тимчасово не містити id або бути порожнім;
    // - відкрита модалка з plotId: скидання тут ламає UX до того, як handleCloseModal очистить вибір.
    if (isPending || isFetching) return;
    if (
      isRealEstateModalOpen
      || isPlotFreeModalOpen
      || isRealEstateInfoModalOpen
      || isMapObjectModalOpen
    ) {
      return;
    }

    setSelectedMapObject(null);
    // Свідомо: selectedMapObject?.id (не цілий selectedMapObject) — цілий обʼєкт
    // перестворюється зі snapshot mapObjects → зайві setSelected + highlight teardown.
    // Порівняння полів іде з closure тієї ж рендер-ітерації, що mapObjects (теж у deps).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- id + mapObjects, not whole selected
  }, [
    mapObjects,
    selectedMapObject?.id,
    isPending,
    isFetching,
    isRealEstateModalOpen,
    isPlotFreeModalOpen,
    isRealEstateInfoModalOpen,
    isMapObjectModalOpen,
    sceneRef,
    setSelectedMapObject,
  ]);
}

export interface UseReturnToMapObjectRestoreParams {
  returnToMapObjectId: number | null;
  mapObjects: MapObject[];
  setSelectedMapObject: (obj: MapObject | null) => void;
  openModal: (name: 'realEstateInfo') => void;
  clearReturnToMapObjectId: () => void;
}

export function useReturnToMapObjectRestore({
  returnToMapObjectId,
  mapObjects,
  setSelectedMapObject,
  openModal,
  clearReturnToMapObjectId,
}: UseReturnToMapObjectRestoreParams): void {
  // Відновлення модалки будинку після повернення з інтерʼєру (кнопка назад у RoomCanvas)
  useEffect(() => {
    if (!returnToMapObjectId || mapObjects.length === 0) return;

    const obj = mapObjects.find((o) => o.id === returnToMapObjectId);
    if (obj) {
      setSelectedMapObject(obj);
      openModal('realEstateInfo');
    }
    clearReturnToMapObjectId();
  // Тригер: return id + length (не цілий mapObjects — новий snapshot щорефетч
  // → повторний openModal). setSelected/openModal/clear — виклик 1× на restore.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- restore once per return id
  }, [returnToMapObjectId, mapObjects.length]);
}
